'use client';
import React, { useEffect, useRef } from 'react';

const FEATURES = [
  {
    id: 'live-feed',
    title: 'Real-Time Global Feed',
    desc: 'Every luck incident posted by a unique visitor appears instantly. Watch the world\'s luck unfold in real-time.',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#3DFFC4" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <polyline points="12 6 12 12 16 14" />
      </svg>
    ),
    color: '#3DFFC4',
    span: 'lg:col-span-2 lg:row-span-1',
    stat: '< 1s',
    statLabel: 'post to live feed',
  },
  {
    id: 'luck-map',
    title: 'Live Luck Map',
    desc: 'Incidents pinned to their origin city. See where luck clusters, spikes, and travels across the globe.',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#B4A0FF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="10" r="3" />
        <path d="M12 2a8 8 0 0 1 8 8c0 5.25-8 14-8 14S4 15.25 4 10a8 8 0 0 1 8-8z" />
      </svg>
    ),
    color: '#B4A0FF',
    span: 'lg:col-span-1 lg:row-span-2',
    stat: '140+',
    statLabel: 'countries reporting',
  },
  {
    id: 'incident-types',
    title: 'Incident Type Tagging',
    desc: 'Lucky Escape, Windfall, Serendipity, Near Miss — categorise your moment so patterns emerge.',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#3DFFC4" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
        <line x1="7" y1="7" x2="7.01" y2="7" />
      </svg>
    ),
    color: '#3DFFC4',
    span: 'lg:col-span-1 lg:row-span-1',
    stat: '8',
    statLabel: 'incident categories',
  },
  {
    id: 'anonymous',
    title: 'Anonymous Posting',
    desc: 'No account required. Post your luck incident as a unique visitor — no sign-up, no tracking, just your story.',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#B4A0FF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
        <circle cx="12" cy="7" r="4" />
      </svg>
    ),
    color: '#B4A0FF',
    span: 'lg:col-span-1 lg:row-span-1',
    stat: '100%',
    statLabel: 'anonymous by default',
  },
  {
    id: 'patterns',
    title: 'Luck Pattern Analytics',
    desc: 'Aggregate data reveals when, where, and how luck strikes. Trends by city, season, incident type, and more.',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#3DFFC4" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" />
      </svg>
    ),
    color: '#3DFFC4',
    span: 'lg:col-span-2 lg:row-span-1',
    stat: '47k+',
    statLabel: 'incidents analysed',
  },
];

export default function FeaturesBento() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('is-visible');
        });
      },
      { threshold: 0.1 }
    );
    sectionRef?.current?.querySelectorAll('.reveal-up')?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-24 relative">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: 'radial-gradient(ellipse at 50% 50%, rgba(61,255,196,0.04) 0%, transparent 70%)' }}
      />
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16 reveal-up">
          <span className="badge-mint text-xs font-display font-700 px-3 py-1.5 rounded-full uppercase tracking-wider">
            Platform features
          </span>
          <h2 className="mt-4 text-[clamp(2rem,4vw,3rem)] font-display font-700 text-iwhite leading-tight tracking-tight">
            Post. Tag. Explore.<br />
            <span style={{ color: '#B4A0FF' }}>The world&apos;s luck, mapped.</span>
          </h2>
          <p className="mt-4 text-lg text-white/50 font-body max-w-xl mx-auto">
            Every feature built around one goal — making it effortless for unique visitors to log and explore luck incidents globally.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 auto-rows-[200px]">
          {FEATURES?.map((f, i) => (
            <div
              key={f?.id}
              className={`bento-card glass rounded-2xl p-6 flex flex-col justify-between relative overflow-hidden reveal-up delay-${(i + 1) * 100} ${f?.span}`}
            >
              <div className="flex items-start justify-between">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ background: `${f?.color}15`, border: `1px solid ${f?.color}30` }}
                >
                  {f?.icon}
                </div>
                <div className="text-right">
                  <div className="text-2xl font-display font-700" style={{ color: f?.color }}>{f?.stat}</div>
                  <div className="text-[10px] text-white/30 font-body">{f?.statLabel}</div>
                </div>
              </div>

              <div>
                <h3 className="text-base font-display font-700 text-iwhite mb-1.5">{f?.title}</h3>
                <p className="text-sm text-white/50 font-body leading-relaxed">{f?.desc}</p>
              </div>

              <div
                className="absolute -bottom-8 -right-8 w-32 h-32 rounded-full pointer-events-none"
                style={{ background: `radial-gradient(circle, ${f?.color}18 0%, transparent 70%)` }}
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}